import {MT_CREATIVE, MT_LINK_ID_KEY, RSM_KEY} from '@web-landing/constants';

import {
  getCookie,
  MT_BUNDLE_ID,
  MT_REFERRER,
  RSM_QUERY,
  TMR_LVID,
  waitForCookies,
  YM_UID,
} from '@web-landing/utils/cookie';

import {logs, type Subs} from '@web-landing/analytics/scripts/analytic';

/**
 * Скрипты для генерации пикселя с трэкинг-ссылками для разных кампаний MyTracker
 *
 * UNLABELED_COMPANY_ID - айди кампании, которая фиксирует пользователей, которые пришли
 * по наразмеченным ссылкам
 */
const TRACKING_LINK_BASE = 'https://trk.mail.ru/c/';
const UNLABELED_COMPANY_ID = 'nubrn2';

const formatMtCreative = (
  uid: string | null | undefined,
  lvid: string | null | undefined,
) => `[${uid || 'undefined'},${lvid || 'undefined'}]`;

type PixelLinkData = {
  src: string;
  subs: Subs;
};

const getSubs = (params: URLSearchParams): Subs => {
  const subs: Subs = ['', '', ''];
  for (let i = 0; i < subs.length; i++) {
    subs[i] = params.get(`mt_sub${i + 1}`) ?? '';
  }
  return subs;
};

const generatePixelLinkForLabeledUser = (
  queryParams: URLSearchParams,
  deeplinkCatalogUrl: string,
): PixelLinkData => {
  const params = new URLSearchParams(queryParams);
  const mtLinkId = params.get(MT_LINK_ID_KEY) as string;
  const mtBundleId = getCookie(MT_BUNDLE_ID);

  params.delete(RSM_KEY);
  params.delete(MT_LINK_ID_KEY);
  params.append('mt_no_redirect', '1');

  if (mtBundleId) {
    params.append('mt_deeplink', `${deeplinkCatalogUrl}/app/${mtBundleId}`);
  }

  params.append(
    MT_CREATIVE,
    formatMtCreative(getCookie(YM_UID), getCookie(TMR_LVID)),
  );

  return {
    src: `${TRACKING_LINK_BASE}${mtLinkId}?${decodeURIComponent(
      params.toString(),
    )}`,
    subs: getSubs(params),
  };
};

const generatePixelLinkForUnlabeledUser = (
  deeplinkCatalogUrl: string,
): PixelLinkData => {
  const url = new URL(`${TRACKING_LINK_BASE}${UNLABELED_COMPANY_ID}`);
  url.searchParams.append('mt_no_redirect', '1');

  const referrer = getCookie(MT_REFERRER);

  if (referrer) {
    url.searchParams.append('mt_sub1', referrer);
  }

  const ymUid = getCookie(YM_UID);

  if (ymUid) {
    url.searchParams.append('mt_sub2', ymUid);
  }

  const lvid = getCookie(TMR_LVID);

  if (lvid) {
    url.searchParams.append('mt_sub3', lvid);
  }

  const mtBundleId = getCookie(MT_BUNDLE_ID);

  if (mtBundleId) {
    url.searchParams.append(
      'mt_deeplink',
      `${deeplinkCatalogUrl}/app/${mtBundleId}`,
    );
  }

  url.searchParams.append(MT_CREATIVE, formatMtCreative(ymUid, lvid));
  return {
    src: decodeURIComponent(url.href),
    subs: getSubs(url.searchParams),
  };
};

const addPixel = (pixel: HTMLImageElement) => {
  const footer = document.querySelector('footer');
  if (!footer) {
    return console.error('Footer is not defined!');
  }
  return footer.appendChild(pixel);
};

// userIp - может потребоваться для формирования пикселя совместно с параметров mt_deeplink
// mt_device_ip={userIp}
export const generateMyTrackerPixel = async (
  deeplinkCatalogUrl: string,
  userIp?: string | null,
) => {
  if (typeof document === 'undefined') {
    return;
  }

  await waitForCookies({names: [YM_UID, TMR_LVID]});

  const existingPixel = document.getElementById('pixel');

  if (existingPixel) {
    return;
  }

  const pixel = document.createElement('img');
  pixel.id = 'pixel';
  pixel.width = 1;
  pixel.height = 1;

  const rsmCookieValue = getCookie(RSM_QUERY);

  const rsmQueryParams = new URLSearchParams(rsmCookieValue ?? '');

  const mtLinkId = rsmQueryParams.has(MT_LINK_ID_KEY)
    ? (rsmQueryParams.get(MT_LINK_ID_KEY) as string)
    : UNLABELED_COMPANY_ID;

  const {src, subs} = rsmQueryParams.has(MT_LINK_ID_KEY)
    ? generatePixelLinkForLabeledUser(rsmQueryParams, deeplinkCatalogUrl)
    : generatePixelLinkForUnlabeledUser(deeplinkCatalogUrl);

  pixel.src = src;

  addPixel(pixel);
  logs.addMTPixel(userIp, mtLinkId, subs);

  return;
};
